import {FiBell, FiCheckCircle, FiFileText, FiGlobe, FiInfo, FiLayout, FiList, FiMonitor, FiTool} from "react-icons/fi";
import {LuChartColumnIncreasing} from "react-icons/lu";
import {PiFlagBold} from "react-icons/pi";

import {DotVariant} from "@/components/dot/Dot.types.ts";
import {URL_ORIGINAL_WEBSITE, URL_UPSTART_AI} from "@/lib/links.ts";
import {BulkCreateChecksItemInput} from "@/lib/queries/checks.ts";
import {
  CheckCategory,
  GroupCheckDownCondition,
  MonitoringServiceType,
  UptimeGradeThreshold,
} from "@/lib/types/checks.ts";
import {
  AlertPriority,
  AnalyzerAlertPriorityDisplay,
  AnalyzerEscalationTier,
  AnalyzerReportBadgeDisplay,
  AnalyzerSensitivityDefaultRow,
  AnalyzerTab,
  AnalyzerToolLabel,
  AnalyzerUsageChipState,
  CheckTypeFamily,
  EscalationTier,
  ProposedCheck,
  ProposedCheckColumnField,
  ReportBadgeStatus,
  ReportDomainOverview,
  ReportSectionPreset,
  ReportSectionType,
  ReportTag,
} from "@/pages/analyzer/Analyzer.types.ts";

export const ANALYZER_TOKEN_REUSE_MARGIN_SECONDS = 10;
const IPV4_OCTET = String.raw`(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)`;
export const ANALYZER_URL_PATTERN = new RegExp(
  String.raw`^(https?:\/\/)((?:[a-zA-Z0-9_\-.]+\.[a-zA-Z]{2,})|(?:${IPV4_OCTET}(?:\.${IPV4_OCTET}){3})|(?:\[[0-9a-f:]+\]))(:\d{2,5})?([/?#]\S*)?$`,
  "i"
);
export const ANALYZER_PRIVATE_IPV4_PATTERN = /^(?:0\.|10\.|127\.|169\.254\.|192\.168\.|172\.(?:1[6-9]|2\d|3[01])\.)/;
export const ANALYZER_MARKDOWN_PATTERN =
  /^ {0,3}(#{1,6}\s|[-*+]\s|\d+\.\s|>\s|```|\|)|\*\*|__|\[[^\]]+\]\([^)]+\)|`[^`]+`/m;
export const ANALYZER_DEFAULT_LOCATIONS_CAP = 15;
export const ANALYZER_TOAST_DURATION_MS = 8000;
export const ANALYZER_TAG_NAME_MAX_LENGTH = 100;
export const ANALYZER_MAX_TAGS_PER_CHECK = 20;
export const ANALYZER_REVISION_MAX_LENGTH = 2000;
export const ANALYZER_POLL_EDIT_INTRO = "Sure — pick a different option and I'll re-tune the Checks.";
export const ANALYZER_CHECK_TYPE_TAB_ALL = "all";

export const ANALYZER_BASE_URL = URL_UPSTART_AI ? `${URL_UPSTART_AI}/api` : `${URL_ORIGINAL_WEBSITE}/api/analyzer`;
export const ANALYZER_STREAM_URL = `${ANALYZER_BASE_URL}/analyze/stream`;
export const ANALYZER_RESPOND_URL = `${ANALYZER_BASE_URL}/analyze/respond`;
export const ANALYZER_JWT_URL = `${URL_ORIGINAL_WEBSITE}/analyzer/jwt`;

export const ANALYZER_TAB_ITEMS: {id: AnalyzerTab; label: string}[] = [
  {id: AnalyzerTab.SELECTION, label: "Checks"},
  {id: AnalyzerTab.REPORT, label: "Report"},
];

export const ANALYZER_REPORT_TITLE = "Monitoring Analysis Report";

export const ANALYZER_REPORT_SUMMARY_PRESET: ReportSectionPreset = {title: "Executive Summary", icon: FiFileText};
export const ANALYZER_REPORT_OVERVIEW_PRESET: ReportSectionPreset = {title: "Domain Overview", icon: FiGlobe};
export const ANALYZER_REPORT_SECTION_PRESETS: Record<ReportSectionType, ReportSectionPreset> = {
  [ReportSectionType.SITE_ARCHITECTURE]: {title: "Site Architecture & Key Endpoints", icon: FiLayout},
  [ReportSectionType.DEPENDENCIES]: {title: "Third-Party Dependencies", icon: FiMonitor},
};
export const ANALYZER_REPORT_CHECKS_PRESET: ReportSectionPreset = {
  title: "Recommended Checks",
  icon: FiCheckCircle,
};
export const ANALYZER_REPORT_MANUAL_CHECKS_PRESET: ReportSectionPreset = {title: "Manual Setup", icon: FiTool};
export const ANALYZER_REPORT_ALERTS_PRESET: ReportSectionPreset = {title: "Alert Configuration", icon: FiBell};
export const ANALYZER_REPORT_SENSITIVITY_PRESET: ReportSectionPreset = {
  title: "Sensitivity Settings",
  icon: LuChartColumnIncreasing,
};
export const ANALYZER_REPORT_STATUS_PAGE_PRESET: ReportSectionPreset = {title: "Status Page", icon: PiFlagBold};
export const ANALYZER_REPORT_CHECK_SUMMARY_PRESET: ReportSectionPreset = {title: "Check Summary", icon: FiList};
export const ANALYZER_REPORT_DISCLAIMER_PRESET: ReportSectionPreset = {title: "Disclaimer", icon: FiInfo};
export const ANALYZER_PDF_UNNUMBERED_TITLES = [
  ANALYZER_REPORT_SUMMARY_PRESET.title,
  ANALYZER_REPORT_OVERVIEW_PRESET.title,
  ANALYZER_REPORT_DISCLAIMER_PRESET.title,
];

export const ANALYZER_ESCALATION_TIERS: AnalyzerEscalationTier[] = [
  {
    tier: EscalationTier.TIER_1,
    waitTime: "Immediate",
    channels: "Push notification, SMS",
    contacts: "Website administrator / technical owner",
  },
  {
    tier: EscalationTier.TIER_2,
    waitTime: "5 minutes",
    channels: "Email, Teams/Slack",
    contacts: "Business owner for affected area",
  },
  {
    tier: EscalationTier.TIER_3,
    waitTime: "15 minutes",
    channels: "Phone call, email",
    contacts: "Operations lead or IT/security owner",
  },
  {
    tier: EscalationTier.TIER_4,
    waitTime: "30 minutes",
    channels: "Phone call, email",
    contacts: "Leadership / owner for extended incidents",
  },
];

export const ANALYZER_ALERT_PRIORITY_DISPLAY: Record<AlertPriority, AnalyzerAlertPriorityDisplay> = {
  [AlertPriority.P1]: {label: "P1 - Critical", escalation: "Immediate to Tier 2"},
  [AlertPriority.P2]: {label: "P2 - High", escalation: "5 minutes to Tier 2"},
  [AlertPriority.P3]: {label: "P3 - Medium", escalation: "15 minutes to Tier 2"},
  [AlertPriority.P4]: {label: "P4 - Low", escalation: "Daily digest or business-hours alert"},
};

// HTTP is ranked by `priority`, DNS TXT and MX rows land in P2 as "email records", unlisted types default to P3
export const ANALYZER_ALERT_PRIORITY_TYPES: Partial<Record<AlertPriority, MonitoringServiceType[]>> = {
  [AlertPriority.P1]: [
    MonitoringServiceType.DNS,
    MonitoringServiceType.SSL_CERT,
    MonitoringServiceType.SMTP,
    MonitoringServiceType.TRANSACTION,
  ],
  [AlertPriority.P2]: [
    MonitoringServiceType.PAGESPEED,
    MonitoringServiceType.CLOUDSTATUS,
    MonitoringServiceType.API,
    MonitoringServiceType.WEBHOOK,
  ],
  [AlertPriority.P4]: [
    MonitoringServiceType.RDAP,
    MonitoringServiceType.WHOIS,
    MonitoringServiceType.BLACKLIST,
    MonitoringServiceType.MALWARE,
    MonitoringServiceType.HEARTBEAT,
  ],
};

export const ANALYZER_SENSITIVITY_DEFAULT_ROWS: AnalyzerSensitivityDefaultRow[] = [
  {
    label: "HTTP(S)",
    types: [MonitoringServiceType.HTTP],
    locations: 5,
    sensitivity: "3 of 5 must fail",
    retries: "2 retries",
  },
  {
    label: "Transaction",
    types: [MonitoringServiceType.TRANSACTION],
    locations: 3,
    sensitivity: "2 of 3 must fail",
    retries: "1 retry",
  },
  {
    label: "SSL/DNS/RDAP",
    types: [MonitoringServiceType.SSL_CERT, MonitoringServiceType.DNS, MonitoringServiceType.RDAP],
    probes: "Default",
    sensitivity: "Default",
    retries: "Default",
  },
  {
    label: "Ping/TCP",
    types: [MonitoringServiceType.ICMP, MonitoringServiceType.TCP],
    locations: 5,
    sensitivity: "3 of 5 must fail",
    retries: "2 retries",
  },
  {
    label: "API",
    types: [MonitoringServiceType.API],
    locations: 3,
    sensitivity: "2 of 3 must fail",
    retries: "2 retries",
  },
  {
    label: "Heartbeat",
    types: [MonitoringServiceType.HEARTBEAT],
    probes: "Internal script posts to unique check URL",
    sensitivity: "Alert when expected signal is missed",
    retries: "N/A",
  },
  {
    label: "Incoming Webhook",
    types: [MonitoringServiceType.WEBHOOK],
    probes: "Internal script posts event state to unique webhook URL",
    sensitivity: "Alert immediately when state_is_up=false",
    retries: "N/A",
  },
];

export const ANALYZER_REPORT_DISCLAIMER =
  "This plan is based on publicly observable data gathered at analysis time and on Uptime.com's Check capabilities. " +
  'Items marked "Likely" are inferred from the kind of business rather than observed. ' +
  "Some details may be inaccurate or out of date. " +
  "Review URLs, expected keywords, and configurations before creating Checks.";

export const ANALYZER_REPORT_LIKELY_TAG: ReportTag = {label: "Likely"};

export const ANALYZER_TIER_LABELS: Record<CheckCategory, string> = {
  [CheckCategory.BASIC]: "Basic",
  [CheckCategory.ADVANCED]: "Advanced",
};

export const ANALYZER_CHECK_TYPE_FAMILIES: Record<CheckTypeFamily, MonitoringServiceType[]> = {
  [CheckTypeFamily.WEBSITE]: [MonitoringServiceType.HTTP],
  [CheckTypeFamily.DNS_SSL]: [MonitoringServiceType.DNS, MonitoringServiceType.SSL_CERT],
  [CheckTypeFamily.MAIL]: [MonitoringServiceType.SMTP, MonitoringServiceType.IMAP, MonitoringServiceType.POP],
  [CheckTypeFamily.DOMAIN]: [
    MonitoringServiceType.RDAP,
    MonitoringServiceType.WHOIS,
    MonitoringServiceType.BLACKLIST,
    MonitoringServiceType.MALWARE,
  ],
  [CheckTypeFamily.API_ENDPOINTS]: [MonitoringServiceType.API],
  [CheckTypeFamily.USER_JOURNEYS]: [MonitoringServiceType.TRANSACTION],
  [CheckTypeFamily.PAGE_PERFORMANCE]: [MonitoringServiceType.PAGESPEED],
  [CheckTypeFamily.REAL_USER_EXPERIENCE]: [MonitoringServiceType.RUM2],
  [CheckTypeFamily.DEPENDENCIES]: [MonitoringServiceType.CLOUDSTATUS],
  [CheckTypeFamily.PROCESS]: [MonitoringServiceType.HEARTBEAT, MonitoringServiceType.WEBHOOK],
};

export const ANALYZER_COVERAGE_MAX_SURFACES = 4;

export const ANALYZER_REPORT_OVERVIEW_FIELDS: {
  field: keyof Omit<ReportDomainOverview, "domain">;
  label: string;
}[] = [
  {field: "primary_website", label: "Primary Website"},
  {field: "ssl_certificate", label: "SSL Certificate"},
  {field: "registration", label: "Registration"},
];

export const ANALYZER_REPORT_BADGE_DISPLAY: Record<ReportBadgeStatus, AnalyzerReportBadgeDisplay> = {
  [ReportBadgeStatus.SUCCESS]: {label: "Live", dot: "green"},
  [ReportBadgeStatus.ERROR]: {label: "Unverified", dot: "yellow"},
};

export const ANALYZER_PLACEHOLDER_FIELDS: ProposedCheckColumnField[] = [
  "monitoring_service_type",
  "msp_address",
  "name",
];

export const ANALYZER_CHECK_TYPE_DEFAULTS: Partial<Record<MonitoringServiceType, Partial<BulkCreateChecksItemInput>>> =
  {
    [MonitoringServiceType.HTTP]: {msp_status_code: "200"},
    [MonitoringServiceType.SSL_CERT]: {msp_threshold: 30},
    [MonitoringServiceType.RDAP]: {msp_threshold: 60},
    [MonitoringServiceType.PAGESPEED]: {uptime_grade_threshold: UptimeGradeThreshold.B},
  };

const PLACEHOLDER_TYPES: MonitoringServiceType[] = [
  MonitoringServiceType.HTTP,
  MonitoringServiceType.HTTP,
  MonitoringServiceType.HTTP,
  MonitoringServiceType.HTTP,
  MonitoringServiceType.API,
  MonitoringServiceType.DNS,
  MonitoringServiceType.API,
  MonitoringServiceType.DNS,
];

export const ANALYZER_PLACEHOLDER_CHECKS: ProposedCheck[] = PLACEHOLDER_TYPES.map((type, index) => ({
  id: `placeholder-${index}`,
  monitoring_service_type: type,
  msp_address: "Cell data",
  name: "Cell data",
  justification: "",
  msp_interval: 0,
}));

export const ANALYZER_USAGE_CHIP_INDICATOR_VARIANTS: Record<AnalyzerUsageChipState, DotVariant> = {
  [AnalyzerUsageChipState.UNDER]: "green",
  [AnalyzerUsageChipState.AT_LIMIT]: "orange",
  [AnalyzerUsageChipState.OVER]: "red",
};

export const ANALYZER_TOOL_LABELS: Record<string, AnalyzerToolLabel> = {
  fetch_url: {active: "Fetching URL", complete: "Fetched URL"},
  whois_lookup: {active: "Running WHOIS Lookup", complete: "Ran WHOIS Lookup"},
  dns_lookup: {active: "Running DNS Lookup", complete: "Ran DNS Lookup"},
  ssl_info: {active: "Checking SSL Info", complete: "Checked SSL Info"},
  extract_page_links: {active: "Extracting Page Links", complete: "Extracted Page Links"},
  discover_subdomains: {active: "Discovering Subdomains", complete: "Discovered Subdomains", summaryArgs: ["domain"]},
  port_check: {active: "Checking Ports", complete: "Checked Ports"},
};

export const ANALYZER_TOOL_GROUP_FALLBACK_TITLE: AnalyzerToolLabel = {
  active: "Gathering details",
  complete: "Gathered details",
};

export const ANALYZER_CHECK_VALUE_TITLES: Partial<Record<MonitoringServiceType, string>> = {
  [MonitoringServiceType.TRANSACTION]: "Steps",
  [MonitoringServiceType.GROUP]: "Contains",
};

export const ANALYZER_JUSTIFIED_CHECK_TYPES: MonitoringServiceType[] = [
  MonitoringServiceType.TRANSACTION,
  MonitoringServiceType.HEARTBEAT,
  MonitoringServiceType.WEBHOOK,
];

export const ANALYZER_CHECK_FIELD_TITLES: Record<string, string> = {
  name: "Name",
  justification: "Condition",
  monitoring_service_type: "Type",
  msp_address: "Target",
  msp_interval: "Interval (min)",
  msp_sensitivity: "Sensitivity",
  num_locations: "Locations",
  msp_port: "Port",
  msp_dns_record_type: "DNS Record",
  msp_expect_string: "Expect String",
  monitoring_groups: "Locations",
  tags: "Tags",
  contact_groups: "Contact Groups",
  group_check_services: "Members",
  group_check_id: "Group",
} satisfies Record<ProposedCheckColumnField | "monitoring_groups" | "group_check_services" | "group_check_id", string>;

export const ANALYZER_USAGE_CHIP_SHOW_RATIO = 0.8;

export const ANALYZER_GROUP_CHECK_LIMIT_NAME = "Group Check";

export const ANALYZER_GROUP_CONDITION_COUNTS: Partial<Record<GroupCheckDownCondition, number>> = {
  [GroupCheckDownCondition.ANY]: 1,
  [GroupCheckDownCondition.TWO]: 2,
  [GroupCheckDownCondition.THREE]: 3,
  [GroupCheckDownCondition.FOUR]: 4,
  [GroupCheckDownCondition.FIVE]: 5,
  [GroupCheckDownCondition.TEN]: 10,
};
export const ANALYZER_GROUP_CONDITION_PERCENTS: Partial<Record<GroupCheckDownCondition, number>> = {
  [GroupCheckDownCondition.ONE_PCT]: 1,
  [GroupCheckDownCondition.THREE_PCT]: 3,
  [GroupCheckDownCondition.FIVE_PCT]: 5,
  [GroupCheckDownCondition.TEN_PCT]: 10,
  [GroupCheckDownCondition.TWENTYFIVE_PCT]: 25,
  [GroupCheckDownCondition.FIFTY_PCT]: 50,
};

export const ANALYZER_USAGE_CHIP_DESCRIPTIONS: Record<string, string> = {
  [`${ANALYZER_TIER_LABELS[CheckCategory.BASIC]} Check`]: "Core monitoring — HTTP, DNS, Ping, SSL, TCP and more.",
  [`${ANALYZER_TIER_LABELS[CheckCategory.ADVANCED]} Check`]:
    "Deeper, higher-cost Checks — Transaction, Page Speed, RUM and more.",
  [ANALYZER_GROUP_CHECK_LIMIT_NAME]: "One Alert per business function when its member Checks fail.",
  "Real User Monitoring Check": "Real-visitor page loads, reported by a snippet on your site.",
};

export const ANALYZER_SILENT_MUTATION = {ignoreResults: true, context: {noToast: true, noLoadingCursor: true}};
